/**
 * friendlyError.ts
 *
 * Translates raw technical errors, network drops, proxy failures, timeouts,
 * and S3 XML errors into clean, polite, and professional English messages.
 */

export function getFriendlyErrorMessage(error: unknown, fallback: string = 'An unexpected error occurred. Please try again.'): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as {
    code?: string;
    message?: string;
    response?: {
      status?: number;
      data?: any;
    };
  };

  const status = err.response?.status;
  const rawMsg = String(err.response?.data?.message || err.response?.data?.error || err.message || '');

  // 1. Network / Connection Down (No server response or network unreachable)
  if (!err.response && (err.code === 'ERR_NETWORK' || rawMsg.toLowerCase().includes('network error'))) {
    return 'Unable to connect to the server. Please check your internet connection and try again in a moment.';
  }

  // 2. Request / Client Timeout (30s axios timeout or connection abort)
  if (err.code === 'ECONNABORTED' || rawMsg.toLowerCase().includes('timeout')) {
    return 'Request timed out. The server is taking longer than expected to respond. Please try again.';
  }

  // 3. Server High Load / Bad Gateway / Gateway Timeout (502, 503, 504 or Apache Proxy Error)
  if (status === 502 || status === 503 || status === 504 || rawMsg.toLowerCase().includes('proxy error')) {
    return 'The server is currently experiencing high load. Please wait a moment and try again.';
  }

  // 4. Rate Limiting / Too Many Attempts (429)
  if (status === 429) {
    return 'Too many attempts. For your security, please wait a few minutes before trying again.';
  }

  // 5. File Too Large / Payload Exceeded (413)
  if (status === 413) {
    return 'The selected file is too large. Please choose a smaller file and try again.';
  }

  // 6. Forbidden / Access Denied (403)
  if (status === 403) {
    return err.response?.data?.message || 'You do not have permission to perform this action.';
  }

  // 7. Clean server-provided message if available and not a raw technical dump
  const serverMsg = err.response?.data?.message || err.response?.data?.error;
  if (serverMsg && typeof serverMsg === 'string' && !serverMsg.includes('<!DOCTYPE') && !serverMsg.includes('Proxy Error')) {
    return serverMsg;
  }

  return fallback;
}

/**
 * Parses errors from direct S3/MinIO uploads (which return XML or Blobs rather than JSON)
 */
export async function parseS3UploadError(error: unknown): Promise<string> {
  if (!error || typeof error !== 'object') {
    return 'Unable to upload file to storage. Please try again.';
  }

  const err = error as {
    code?: string;
    message?: string;
    response?: {
      status?: number;
      data?: any;
    };
  };

  let data = err.response?.data;
  const status = err.response?.status;

  // Handle Blob data if responseType was binary
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      data = await data.text();
    } catch {
      data = '';
    }
  }

  if (typeof data === 'string' && data.includes('<Error>')) {
    if (data.includes('<Code>EntityTooLarge</Code>') || status === 413) {
      return 'The selected file exceeds the permitted size limit (max 500 MB). Please choose a smaller file.';
    }
    if (data.includes('<Code>AccessDenied</Code>')) {
      if (data.includes('content-length-range')) {
        return 'The file size is outside the permitted range for this upload.';
      }
      if (data.includes('Content-Type')) {
        return 'Invalid file format. The uploaded file type is not supported.';
      }
      return 'Upload permission denied. Your session may have expired. Please sign in again.';
    }
    if (data.includes('<Code>RequestTimeout</Code>')) {
      return 'Storage upload connection timed out. Please check your network speed and try again.';
    }
  }

  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return 'Upload timed out. Please check your internet connection and try again.';
  }

  if (status === 413) {
    return 'The selected file exceeds the allowed upload limit. Please choose a smaller file.';
  }

  return getFriendlyErrorMessage(error, 'Unable to complete file upload. Please check your connection and try again.');
}
